import axios from 'axios';

/*
 * Curs valutar de referință BNR (date publice) pentru convertorul PWA.
 *
 * Sursă: endpoint public existent `${NEXT_PUBLIC_API}/api/marketdata/cursbnr/find`
 * (fără parametru → cea mai recentă zi, cu variația față de ziua precedentă).
 *
 * Strategie „stale-while-revalidate" pe CLIENT (localStorage): datele sunt
 * publice + ne-sensibile → le putem păstra offline și afișa cu timestamp.
 * Nu se cache-uiesc în service worker (sunt cross-origin, clasa 3 = network-only).
 */
const CACHE_KEY = 'conso_fx_bnr_v1';

// Ordinea de afișare — cele mai folosite primele; restul rămân disponibile.
export const CURRENCY_ORDER = [
  'eur', 'usd', 'gbp', 'chf', 'ron',
  'huf', 'pln', 'czk', 'bgn', 'try', 'mdl', 'uah',
  'cad', 'aud', 'nzd', 'jpy', 'cny', 'sek', 'nok', 'dkk',
  'aed', 'zar', 'xau',
];

// Câmpuri care NU sunt valute în payload.
const NON_CURRENCY = ['id', 'data', 'change'];

export function loadCachedRates() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function saveCachedRates(payload) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch (e) {
    /* quota / private mode — ignorăm, mergem doar pe rețea */
  }
}

/*
 * Normalizează răspunsul API într-un obiect stabil:
 * { date: '02-07-2026', rates: { eur: 5.07, usd: 4.6, ... }, change: {...} }
 * RON e implicit 1 (referința).
 */
function normalize(apiData) {
  const rates = { ron: 1 };
  Object.keys(apiData).forEach((k) => {
    if (NON_CURRENCY.includes(k)) return;
    const val = parseFloat(apiData[k]);
    if (!Number.isNaN(val) && val > 0) rates[k] = val;
  });
  return {
    date: apiData.data || null,
    rates,
    change: apiData.change || {},
    fetchedAt: new Date().toISOString(),
  };
}

/*
 * Întoarce cursul: încearcă rețeaua, cade pe cache la eroare.
 * Returnează { data, fromCache } sau null dacă nu există nici rețea, nici cache.
 */
export async function fetchRates() {
  const url = process.env.NEXT_PUBLIC_API + '/api/marketdata/cursbnr/find';
  try {
    const res = await axios.get(url, { timeout: 8000 });
    if (res.data && res.data.status && res.data.data) {
      const normalized = normalize(res.data.data);
      saveCachedRates(normalized);
      return { data: normalized, fromCache: false };
    }
  } catch (e) {
    /* offline / eroare → fallback pe cache */
  }
  const cached = loadCachedRates();
  return cached ? { data: cached, fromCache: true } : null;
}

/*
 * Conversie client-side, aceeași logică ca CurrencyConversion (server):
 * rezultat = sumă × rate[from] / rate[to]  (rate = RON per 1 unitate; RON = 1)
 */
export function convert(amount, from, to, rates) {
  const f = rates[from];
  const t = rates[to];
  if (!f || !t || Number.isNaN(amount)) return null;
  return (amount * f) / t;
}

/*
 * Istoricul cursului pentru o valută, pentru graficul de evoluție.
 *
 * Sursă: `${NEXT_PUBLIC_API}/api/marketdata/cursbnr/range` (public, aceeași
 * familie ca /find). Întoarce doar zile lucrătoare — ~21 de puncte pentru 30 de
 * zile calendaristice, deci axa nu e continuă și nu trebuie tratată ca atare.
 *
 * GOTCHA: valorile vin cu zgomot de virgulă mobilă (5.2507999999999999…),
 * de aceea rotunjim la 4 zecimale la intrare, nu la afișare.
 */
const HISTORY_KEY = 'conso_fx_history_v1';

const pad = (n) => String(n).padStart(2, '0');
const toApiDate = (d) => `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;

export async function fetchHistory(currency, days = 30) {
  const cur = (currency || '').toLowerCase();
  if (!cur || cur === 'ron') return null;

  const cacheKey = `${HISTORY_KEY}_${cur}_${days}`;
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);

  const url =
    `${process.env.NEXT_PUBLIC_API}/api/marketdata/cursbnr/range` +
    `?start=${toApiDate(start)}&end=${toApiDate(end)}&currency=${cur}`;

  try {
    const res = await axios.get(url, { timeout: 8000 });
    if (res.data && res.data.status && Array.isArray(res.data.data)) {
      const points = res.data.data
        .map((row) => ({
          date: row.data,
          value: Math.round(parseFloat(row[cur]) * 10000) / 10000,
        }))
        .filter((p) => p.date && !Number.isNaN(p.value));

      if (points.length) {
        try {
          window.localStorage.setItem(cacheKey, JSON.stringify(points));
        } catch (e) {
          /* quota / private mode */
        }
        return { points, fromCache: false };
      }
    }
  } catch (e) {
    /* offline → cache */
  }

  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(cacheKey) : null;
    const cached = raw ? JSON.parse(raw) : null;
    return cached && cached.length ? { points: cached, fromCache: true } : null;
  } catch (e) {
    return null;
  }
}

// Lista de valute disponibile în payload, ordonată.
export function availableCurrencies(rates) {
  const present = Object.keys(rates);
  const ordered = CURRENCY_ORDER.filter((c) => present.includes(c));
  const rest = present.filter((c) => !CURRENCY_ORDER.includes(c)).sort();
  return [...ordered, ...rest];
}
